import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './ScheduleEditor.module.css';
import { useMediaQuery } from '../hooks/useMediaQuery';
// Font Awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faPlus, 
  faMinus,
  faTrash, 
  faSave, 
  faUndo, 
  faCheck,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { 
  getCurrentSchedule, 
  getScheduleVersions,
  saveSchedule, 
  getScheduleFiles, 
  getFileContent, 
  restoreBackup, 
  deleteBackup,
  saveWeeklySchedulesAsDefault,
  getDefaultWeeklySchedules,
  restoreDefaultWeeklySchedules,
  savePrintConfig,
  loadPrintConfig,
  saveNamedPrintConfig,
  loadNamedPrintConfig,
  listNamedPrintConfigs,
  deleteNamedPrintConfig
} from './fileService';
import { weeklyZmanim, getDailyZmanim, ZmanEnum, WeeklyZmanimEnum, titleMapper } from '../calendar/zmanim';
import { formatTime } from '../utils/dateUtils';
import { resolveScheduleTimes, getZmanPreview } from '../utils/dataLogic';
import { useScheduleVersionCheck } from '../hooks/useScheduleVersionCheck';
import StaleVersionBar from './StaleVersionBar';
import { isAutoFontSize } from '../utils/scheduleFontSize';

const SCHEDULE_WATCHED_SECTIONS = ['weeklySchedules', 'datedSchedules', 'news', 'weeklyNotices', 'announcements', 'displayOptions'];

/** Bidi: mixed Hebrew/English fields follow first strong character while editing */
const INPUT_DIR_MIXED = 'auto';
/** Bidi: times, CSS sizes, and other always-LTR values */
const INPUT_DIR_LTR = 'ltr';

function blocksToPreviewHtml(blocks) {
  if (!Array.isArray(blocks)) return '';
  return blocks.map(block => {
    const styleParts = [
      `display:${block.visible ? '' : 'none'}`,
      `font-size:${block.fontSize || '14px'}`,
      `direction: ${block.direction || 'rtl'}`,
      `text-align:${block.textAlign || 'center'}`,
      'padding:0px',
      'unicode-bidi:plaintext',
    ];
    if (block.fontWeight === 'bold') styleParts.push('font-weight:bold');
    const contentWithBreaks = (block.content || '').replace(/\n/g, '<br />');
    return `<div style="${styleParts.join(';')}">\n${contentWithBreaks}\n</div>`;
  }).join('\n');
}

function FontSizeFields({ fontSize, onChange, lastManualRef, itemKey }) {
  const autoSize = isAutoFontSize(fontSize);

  const handleAutoToggle = (e) => {
    if (e.target.checked) {
      if (fontSize && !isAutoFontSize(fontSize)) {
        lastManualRef.current[itemKey] = fontSize;
      }
      onChange('auto');
      return;
    }
    onChange(lastManualRef.current[itemKey] || '');
  };

  return (
    <div className={styles.fontSizeCell}>
      <label className={styles.fontSizeAutoLabel}>
        <input
          type="checkbox"
          checked={autoSize}
          onChange={handleAutoToggle}
        />
        Auto-size to fit
      </label>
      <input
        type="text"
        dir={INPUT_DIR_LTR}
        className={styles.fontSizeInput}
        value={autoSize ? '' : (fontSize || '')}
        onChange={(e) => onChange(e.target.value)}
        disabled={autoSize}
        placeholder="e.g. 0.55em"
        title="Manual font size (em). Enable auto-size to fit long values on one line."
      />
    </div>
  );
}

const ScheduleEditor = ({ activeTab: activeTabProp }) => {
  const [schedule, setSchedule] = useState(null);
  const lastManualFontSizeRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [internalActiveTab, setInternalActiveTab] = useState('weekly');
  
  // Use prop if provided, otherwise use internal state
  const activeTab = activeTabProp !== undefined ? activeTabProp : internalActiveTab;
  const setActiveTab = activeTabProp !== undefined ? () => {} : setInternalActiveTab;
  
  // Additional info state handlers
  const [showSefirasHaomer, setShowSefirasHaomer] = useState(false);
  const [showThreeKapitels, setShowThreeKapitels] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [weeklyNotices, setWeeklyNotices] = useState([]);
  const [announcements, setAnnouncements] = useState(''); // HTML preview only
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Version tracking for optimistic locking
  const [versions, setVersions] = useState({});
  const { isStale } = useScheduleVersionCheck(versions, SCHEDULE_WATCHED_SECTIONS);
  
  // Backup management state
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [backupContent, setBackupContent] = useState(null);
  const [backupError, setBackupError] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Default weekly schedules state
  const [defaultWeeklySchedules, setDefaultWeeklySchedules] = useState(null);
  const [defaultSavedAt, setDefaultSavedAt] = useState(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [restoringDefault, setRestoringDefault] = useState(false);
  const [defaultSuccess, setDefaultSuccess] = useState(false);
  const [defaultError, setDefaultError] = useState('');
  
  // Print configuration state
  const [printConfig, setPrintConfig] = useState({
    selectedSections: [],
    weeklySections: [],
    datedSections: []
  });
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState('');
  const [printSuccess, setPrintSuccess] = useState(false);
  
  // Named print configurations state
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [configName, setConfigName] = useState('');
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Announcement editor state
  const [announcementBlocks, setAnnouncementBlocks] = useState([]);
  const [showAnnouncementEditor, setShowAnnouncementEditor] = useState(false);

  // Week selector state for weekly schedules preview
  const [selectedWeekDate, setSelectedWeekDate] = useState(() => {
    // Initialize to Saturday of current week
    const today = new Date();
    const day = today.getDay();
    const saturday = new Date(today);
    if (day === 6) {
      return saturday;
    } else {
      let diff = (6 - day + 7) % 7;
      if (diff === 0) diff = 7;
      saturday.setDate(today.getDate() + diff);
      return saturday;
    }
  });

  // Helper function to get Saturday of the week for a given date
  const getSaturdayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    if (day === 6) {
      return d;
    } else {
      let diff = (6 - day + 7) % 7;
      if (diff === 0) diff = 7;
      d.setDate(d.getDate() + diff);
      return d;
    }
  };

  // Helper function to navigate weeks
  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeekDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setSelectedWeekDate(newDate);
  };

  // Helper function to format date for display
  const formatWeekDisplay = (date) => {
    const saturday = getSaturdayOfWeek(date);
    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() - 6);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${sunday.toLocaleDateString('en-US', options)} - ${saturday.toLocaleDateString('en-US', options)}`;
  };

  // Helper function to generate zman dropdown options
  const generateZmanOptions = () => {
    return Object.entries(ZmanEnum).map(([key, value]) => (
      <option key={value} value={value}>
        {value} ({titleMapper[value]})
      </option>
    ));
  };

  // Announcement editor functions

  const addAnnouncementBlock = () => {
    const newBlock = {
      id: Date.now(),
      visible: true,
      fontSize: '14px',
      textAlign: 'center',
      direction: 'rtl',
      fontWeight: 'normal',
      content: '',
    };
    setAnnouncementBlocks([...announcementBlocks, newBlock]);
  };

  const updateAnnouncementBlock = (id, field, value) => {
    setAnnouncementBlocks(blocks => 
      blocks.map(block => 
        block.id === id ? { ...block, [field]: value } : block
      )
    );
  };

  const deleteAnnouncementBlock = (id) => {
    setAnnouncementBlocks(blocks => blocks.filter(block => block.id !== id));
  };

  const saveAnnouncementBlocks = () => {
    const cleanBlocks = announcementBlocks.map(({ id, ...rest }) => rest);
    setAnnouncements(blocksToPreviewHtml(announcementBlocks));
    setSchedule(prevSchedule => ({
      ...prevSchedule,
      announcements: cleanBlocks
    }));
    setShowAnnouncementEditor(false);
  };

  const openAnnouncementEditor = () => {
    const blocks = (announcementBlocks.length > 0
      ? announcementBlocks
      : (schedule.announcements && Array.isArray(schedule.announcements) ? schedule.announcements : [])
    ).map((b, i) => ({ ...b, id: b.id || Date.now() + i }));
    setAnnouncementBlocks(blocks);
    setShowAnnouncementEditor(true);
  };

  // Print configuration functions
  const loadPrintConfigData = async () => {
    try {
      setPrintLoading(true);
      setPrintError('');
      
      const config = await loadPrintConfig();
      setPrintConfig(config);
    } catch (err) {
      
      console.error('Error loading print config:', err);
      setPrintError('Failed to load print configuration');
    } finally {
      setPrintLoading(false);
    }
  };

  const savePrintConfigData = async () => {
    try {
      setPrintLoading(true);
      setPrintError('');
      
      await savePrintConfig(printConfig);
      setPrintSuccess(true);
      setTimeout(() => setPrintSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving print config:', err);
      setPrintError('Failed to save print configuration');
    } finally {
      setPrintLoading(false);
    }
  };

  const addSectionToPrint = (sectionKey, sectionType) => {
    const section = sectionType === 'weekly' 
      ? schedule?.weeklySchedules?.[sectionKey]
      : schedule?.datedSchedules?.[sectionKey];
    
    if (section) {
      const newSection = {
        key: sectionKey,
        type: sectionType,
        title: section.sectionTitle,
        sortOrder: section.sortOrder || 0
      };
      
      setPrintConfig(prev => ({
        ...prev,
        selectedSections: [...prev.selectedSections, newSection]
      }));
    }
  };


  const removeSectionFromPrint = (index) => {
    setPrintConfig(prev => ({
      ...prev,
      selectedSections: prev.selectedSections.filter((_, i) => i !== index)
    }));
  };

  const moveSectionUp = (index) => {
    if (index > 0) {
      setPrintConfig(prev => {
        const newSections = [...prev.selectedSections];
        [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
        return {
          ...prev,
          selectedSections: newSections
        };
      });
    }
  };

  const moveSectionDown = (index) => {
    setPrintConfig(prev => {
      if (index < prev.selectedSections.length - 1) {
        const newSections = [...prev.selectedSections];
        [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        return {
          ...prev,
          selectedSections: newSections
        };
      }
      return prev;
    });
  };

  // Named print configuration functions
  const loadSavedConfigs = async () => {
    try {
      setLoadingConfigs(true);
      const configs = await listNamedPrintConfigs();
      setSavedConfigs(configs);
    } catch (err) {
      console.error('Error loading saved configs:', err);
      setPrintError('Failed to load saved configurations');
    } finally {
      setLoadingConfigs(false);
    }
  };

  const saveNamedConfig = async () => {
    if (!configName.trim()) {
      setPrintError('Please enter a configuration name');
      return;
    }

    try {
      setSavingConfig(true);
      setPrintError('');
      
      await saveNamedPrintConfig(configName.trim(), printConfig);
      setPrintSuccess(`Configuration "${configName}" saved successfully!`);
      setConfigName('');
      await loadSavedConfigs(); // Refresh the list
      setTimeout(() => setPrintSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving named config:', err);
      setPrintError('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const loadNamedConfig = async (configName) => {
    try {
      setLoadingConfig(true);
      setPrintError('');
      
      const config = await loadNamedPrintConfig(configName);
      setPrintConfig(config);
      setPrintSuccess(`Configuration "${configName}" loaded successfully!`);
      setTimeout(() => setPrintSuccess(false), 3000);
    } catch (err) {
      console.error('Error loading named config:', err);
      setPrintError('Failed to load configuration');
    } finally {
      setLoadingConfig(false);
    }
  };

  const deleteNamedConfig = async (configName) => {
    if (!window.confirm(`Are you sure you want to delete the configuration "${configName}"?`)) {
      return;
    }

    try {
      setPrintError('');
      await deleteNamedPrintConfig(configName);
      setPrintSuccess(`Configuration "${configName}" deleted successfully!`);
      await loadSavedConfigs(); // Refresh the list
      setTimeout(() => setPrintSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting named config:', err);
      setPrintError('Failed to delete configuration');
    }
  };

  // Fetch the current schedule, versions, and defaults
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [data, serverVersions] = await Promise.all([
        getCurrentSchedule(),
        getScheduleVersions(),
      ]);
      setSchedule(data);
      setVersions(serverVersions);

      if (data.displayOptions) {
        setShowSefirasHaomer(data.displayOptions.showSefirasHaomer || false);
        setShowThreeKapitels(data.displayOptions.showThreeKapitels || false);
      }
      if (data.news) {
        setNewsItems(data.news || []);
      }
      if (data.weeklyNotices) {
        setWeeklyNotices(data.weeklyNotices || []);
      }
      if (data.announcements) {
        setAnnouncements(typeof data.announcements === 'string' ? data.announcements : '');
      }

      // Fetch structured announcement blocks from DB
      try {
        const token = localStorage.getItem('authToken');
        const annRes = await fetch('/api/schedule/section/announcements', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (annRes.ok) {
          const annData = await annRes.json();
          if (Array.isArray(annData.data)) {
            setAnnouncementBlocks(annData.data.map((b, i) => ({ ...b, id: Date.now() + i })));
            setSchedule(prev => ({ ...prev, announcements: annData.data }));
            setAnnouncements(blocksToPreviewHtml(annData.data));
          }
        }
      } catch (annErr) {
        console.warn('Could not load structured announcements:', annErr);
      }

      try {
        const defaultData = await getDefaultWeeklySchedules();
        if (defaultData) {
          setDefaultWeeklySchedules(defaultData.weeklySchedules);
          setDefaultSavedAt(new Date(defaultData.savedAt));
        }
      } catch (defaultErr) {
        console.warn('No default weekly schedules found:', defaultErr);
      }

      setError('');
    } catch (err) {
      setError('Failed to load schedule. Please try again.');
      console.error('Error loading schedule:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Fetch backups when the backups tab is selected
  useEffect(() => {
    if (activeTab === 'backups') {
      fetchBackups();
    }
  }, [activeTab]);

  // Load print config when the print tab is selected
  useEffect(() => {
    if (activeTab === 'print') {
      loadPrintConfigData();
      loadSavedConfigs();
    }
  }, [activeTab, schedule]);
  
  // Fetch the list of backups
  const fetchBackups = async () => {
    try {
      setBackupsLoading(true);
      setBackupError('');
      
      const backupsList = await getScheduleFiles();
      setBackups(backupsList);
    } catch (err) {
      setBackupError('Failed to load backups. Please try again.');
      console.error('Error loading backups:', err);
    } finally {
      setBackupsLoading(false);
    }
  };

  // Save the schedule with optimistic locking
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      setSaveError('');
      
      const result = await saveSchedule(schedule, versions);
      setVersions(prev => ({ ...prev, ...result.versions }));
      setSaveSuccess(true);
      
      if (activeTab === 'backups') {
        fetchBackups();
      }
    } catch (err) {
      if (err.conflict) {
        const section = err.details?.section || 'unknown';
        setSaveError(
          `Conflict: "${section}" was modified by another user. Click Reload to get the latest version.`
        );
      } else {
        setSaveError('Failed to save schedule. Please try again.');
      }
      console.error('Error saving schedule:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Save the current weekly schedules as the default
  const handleSaveDefault = async () => {
    try {
      setSavingDefault(true);
      setDefaultSuccess(false);
      setDefaultError('');
      
      await saveWeeklySchedulesAsDefault(schedule.weeklySchedules);
      
      setDefaultWeeklySchedules(schedule.weeklySchedules);
      setDefaultSavedAt(new Date());
      setDefaultSuccess(true);
    } catch (err) {
      setDefaultError('Failed to save default weekly schedules. Please try again.');
      console.error('Error saving default weekly schedules:', err);
    } finally {
      setSavingDefault(false);
    }
  };
  
  // Restore the default weekly schedules
  const handleRestoreDefault = async () => {
    try {
      setRestoringDefault(true);
      setDefaultSuccess(false);
      setDefaultError('');
      
      const result = await restoreDefaultWeeklySchedules(versions);
      
      setSchedule(result.schedule);
      if (result.versions) {
        setVersions(prev => ({ ...prev, ...result.versions }));
      }
      setDefaultSuccess(true);
    } catch (err) {
      if (err.conflict) {
        setDefaultError('Conflict: schedule was modified elsewhere. Reload and try again.');
      } else {
        setDefaultError('Failed to restore default weekly schedules. Please try again.');
      }
      console.error('Error restoring default weekly schedules:', err);
    } finally {
      setRestoringDefault(false);
    }
  };
  
  // Add keyboard handler for Ctrl+S to save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser save dialog
        handleSave();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [schedule]); // Re-attach when schedule changes to ensure latest state is saved

  // Handle backup/history entry selection
  const handleBackupSelect = async (id) => {
    try {
      setSelectedBackup(id);
      setBackupContent(null);
      
      const entry = await getFileContent(id);
      setBackupContent(entry);
    } catch (err) {
      console.error('Error loading history entry:', err);
    }
  };
  
  // Restore a history entry
  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    
    try {
      setRestoring(true);
      
      await restoreBackup(selectedBackup);
      
      // Reload the full schedule and versions after restore
      await fetchData();
      setActiveTab('weekly');
      setSelectedBackup(null);
      setBackupContent(null);
    } catch (err) {
      console.error('Error restoring from history:', err);
    } finally {
      setRestoring(false);
    }
  };
  
  // Delete a backup
  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    
    try {
      setDeleting(true);
      
      await deleteBackup(selectedBackup);
      
      // Refresh the backups list
      fetchBackups();
      
      // Clear the backup selection
      setSelectedBackup(null);
      setBackupContent(null);
    } catch (err) {
      console.error('Error deleting backup:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Handle changes to weekly schedule sections
  const handleWeeklySectionChange = (sectionKey, field, value) => {
    setSchedule(prevSchedule => ({
      ...prevSchedule,
      weeklySchedules: {
        ...prevSchedule.weeklySchedules,
        [sectionKey]: {
          ...prevSchedule.weeklySchedules[sectionKey],
          [field]: value
        }
      }
    }));
  };

  // Handle changes to weekly schedule items
  const handleWeeklyItemChange = (sectionKey, itemIndex, field, value) => {
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.weeklySchedules[sectionKey].schedule];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        [field]: value
      };
      
      return {
        ...prevSchedule,
        weeklySchedules: {
          ...prevSchedule.weeklySchedules,
          [sectionKey]: {
            ...prevSchedule.weeklySchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Handle changes to dated schedule sections
  const handleDatedSectionChange = (sectionKey, field, value) => {
    setSchedule(prevSchedule => ({
      ...prevSchedule,
      datedSchedules: {
        ...prevSchedule.datedSchedules,
        [sectionKey]: {
          ...prevSchedule.datedSchedules[sectionKey],
          [field]: value
        }
      }
    }));
  };

  // Handle changes to dated schedule items
  const handleDatedItemChange = (sectionKey, itemIndex, field, value) => {
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.datedSchedules[sectionKey].schedule];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        [field]: value
      };
      
      return {
        ...prevSchedule,
        datedSchedules: {
          ...prevSchedule.datedSchedules,
          [sectionKey]: {
            ...prevSchedule.datedSchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Add a new item to a weekly schedule section
  const handleAddWeeklyItem = (sectionKey) => {
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.weeklySchedules[sectionKey].schedule, {
        title: '',
        value: ''
      }];
      
      return {
        ...prevSchedule,
        weeklySchedules: {
          ...prevSchedule.weeklySchedules,
          [sectionKey]: {
            ...prevSchedule.weeklySchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Remove an item from a weekly schedule section
  const handleRemoveWeeklyItem = (sectionKey, itemIndex) => {
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.weeklySchedules[sectionKey].schedule];
      updatedItems.splice(itemIndex, 1);
      
      return {
        ...prevSchedule,
        weeklySchedules: {
          ...prevSchedule.weeklySchedules,
          [sectionKey]: {
            ...prevSchedule.weeklySchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Move a weekly item up
  const handleMoveWeeklyItemUp = (sectionKey, itemIndex) => {
    if (itemIndex === 0) return; // Can't move first item up
    
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.weeklySchedules[sectionKey].schedule];
      // Swap with previous item
      [updatedItems[itemIndex - 1], updatedItems[itemIndex]] = [updatedItems[itemIndex], updatedItems[itemIndex - 1]];
      
      return {
        ...prevSchedule,
        weeklySchedules: {
          ...prevSchedule.weeklySchedules,
          [sectionKey]: {
            ...prevSchedule.weeklySchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Move a weekly item down
  const handleMoveWeeklyItemDown = (sectionKey, itemIndex) => {
    const scheduleItems = schedule.weeklySchedules[sectionKey].schedule;
    if (itemIndex === scheduleItems.length - 1) return; // Can't move last item down
    
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.weeklySchedules[sectionKey].schedule];
      // Swap with next item
      [updatedItems[itemIndex], updatedItems[itemIndex + 1]] = [updatedItems[itemIndex + 1], updatedItems[itemIndex]];
      
      return {
        ...prevSchedule,
        weeklySchedules: {
          ...prevSchedule.weeklySchedules,
          [sectionKey]: {
            ...prevSchedule.weeklySchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Move a dated item up
  const handleMoveDatedItemUp = (sectionKey, itemIndex) => {
    if (itemIndex === 0) return; // Can't move first item up
    
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.datedSchedules[sectionKey].schedule];
      // Swap with previous item
      [updatedItems[itemIndex - 1], updatedItems[itemIndex]] = [updatedItems[itemIndex], updatedItems[itemIndex - 1]];
      
      return {
        ...prevSchedule,
        datedSchedules: {
          ...prevSchedule.datedSchedules,
          [sectionKey]: {
            ...prevSchedule.datedSchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Move a dated item down
  const handleMoveDatedItemDown = (sectionKey, itemIndex) => {
    const scheduleItems = schedule.datedSchedules[sectionKey].schedule;
    if (itemIndex === scheduleItems.length - 1) return; // Can't move last item down
    
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.datedSchedules[sectionKey].schedule];
      // Swap with next item
      [updatedItems[itemIndex], updatedItems[itemIndex + 1]] = [updatedItems[itemIndex + 1], updatedItems[itemIndex]];
      
      return {
        ...prevSchedule,
        datedSchedules: {
          ...prevSchedule.datedSchedules,
          [sectionKey]: {
            ...prevSchedule.datedSchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Add a new dated schedule section
  const handleAddDatedSection = () => {
    const newSectionKey = `custom-${Date.now()}`;
    
    setSchedule(prevSchedule => ({
      ...prevSchedule,
      datedSchedules: {
        ...prevSchedule.datedSchedules,
        [newSectionKey]: {
          sectionTitle: 'New Section',
          sortOrder: Object.keys(prevSchedule.datedSchedules).length + 1,
          displayDateTime: new Date().toISOString(),
          expirationDateTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          schedule: []
        }
      }
    }));
  };

  // Remove a dated schedule section
  const handleRemoveDatedSection = (sectionKey) => {
    setSchedule(prevSchedule => {
      const updatedDatedSchedules = { ...prevSchedule.datedSchedules };
      delete updatedDatedSchedules[sectionKey];
      
      return {
        ...prevSchedule,
        datedSchedules: updatedDatedSchedules
      };
    });
  };

  // Add a new item to a dated schedule section
  const handleAddDatedItem = (sectionKey) => {
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.datedSchedules[sectionKey].schedule, {
        title: '',
        value: ''
      }];
      
      return {
        ...prevSchedule,
        datedSchedules: {
          ...prevSchedule.datedSchedules,
          [sectionKey]: {
            ...prevSchedule.datedSchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Helper to map sectionKey to WeeklyZmanimEnum
  const getWeeklyZmanimEnum = (sectionKey) => {
    switch (sectionKey) {
      case 'fridayNight':
        return WeeklyZmanimEnum.FRIDAY_NIGHT;
      case 'shabbosDay':
      case 'shabbosDay_mevorchim':
      case 'shabbosAfternoon':
        return WeeklyZmanimEnum.SHABBOS_DAY;
      case 'fridayMorning':
        return WeeklyZmanimEnum.FRIDAY_MORNING;
      case 'sunday':
      case 'weekdays':
      default:
        return WeeklyZmanimEnum.WEEKDAYS;
    }
  };

  // Remove an item from a dated schedule section
  const handleRemoveDatedItem = (sectionKey, itemIndex) => {
    setSchedule(prevSchedule => {
      const updatedItems = [...prevSchedule.datedSchedules[sectionKey].schedule];
      updatedItems.splice(itemIndex, 1);
      
      return {
        ...prevSchedule,
        datedSchedules: {
          ...prevSchedule.datedSchedules,
          [sectionKey]: {
            ...prevSchedule.datedSchedules[sectionKey],
            schedule: updatedItems
          }
        }
      };
    });
  };

  // Check if we're on mobile
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // State for expanded sections (for mobile)
  const [expandedSections, setExpandedSections] = useState({});
  
  // Initialize expanded sections based on mobile status
  useEffect(() => {
    if (schedule && isMobile) {
      // On mobile, all sections are collapsed by default
      const initialExpandedState = {};
      
      // Set all sections to collapsed
      if (schedule.weeklySchedules) {
        Object.keys(schedule.weeklySchedules).forEach(key => {
          initialExpandedState[`weekly-${key}`] = false;
        });
      }
      
      if (schedule.datedSchedules) {
        Object.keys(schedule.datedSchedules).forEach(key => {
          initialExpandedState[`dated-${key}`] = false;
        });
      }
      
      setExpandedSections(initialExpandedState);
    } else if (schedule && !isMobile) {
      // On desktop, all sections are expanded by default
      const initialExpandedState = {};
      
      // Set all sections to expanded
      if (schedule.weeklySchedules) {
        Object.keys(schedule.weeklySchedules).forEach(key => {
          initialExpandedState[`weekly-${key}`] = true;
        });
      }
      
      if (schedule.datedSchedules) {
        Object.keys(schedule.datedSchedules).forEach(key => {
          initialExpandedState[`dated-${key}`] = true;
        });
      }
      
      setExpandedSections(initialExpandedState);
    }
  }, [schedule, isMobile]);
  
  // Toggle section expanded state
  const toggleSectionExpanded = (sectionKey, type) => {
    const sectionId = `${type}-${sectionKey}`;
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  
  // Toggle expanded state for an item
  const [expandedItems, setExpandedItems] = useState({});
  
  const toggleItemExpanded = (sectionKey, itemIndex, type) => {
    const itemKey = `${type}-${sectionKey}-${itemIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  // Render loading state
  if (loading) {
    return (
      <div className={styles["schedule-editor"]}>
        <div className={styles["loading-message"]}>Loading schedule...</div>
      </div>
    );
  }

  // Render error state
  if (error) {
    // Check if it's a permission error
    const isPermissionError = error.includes('permission') || error.toLowerCase().includes('unauthorized');
    
    return (
      <div className={styles["schedule-editor"]}>
        <div className={styles["error-message"]}>
          {isPermissionError 
            ? "You don't have permission to access the schedule files. Please check your authentication."
            : error}
        </div>
        
        {isPermissionError && (
          <div className={styles["permission-help"]}>
            <p>To fix this issue, please check your authentication and try logging in again.</p>
            <p>If the problem persists, contact your system administrator.</p>
          </div>
        )}
        
        <button 
          className={styles["retry-button"]}
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  // Render empty state
  if (!schedule) {
    return (
      <div className={styles["schedule-editor"]}>
        <div className={styles["empty-message"]}>No schedule data available.</div>
      </div>
    );
  }

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString();
  };

  return (
    <div className={styles["schedule-editor"]}>
      {isStale && <StaleVersionBar onReload={fetchData} />}
      {activeTabProp === undefined && (
        <>
          <h1>Schedule Editor</h1>
          
          <div className={styles.tabs}>
        <button 
          className={`${styles["tab-button"]} ${activeTab === 'weekly' ? styles.active : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly Schedules
        </button>
        <button 
          className={`${styles["tab-button"]} ${activeTab === 'dated' ? styles.active : ''}`}
          onClick={() => setActiveTab('dated')}
        >
          Dated Schedules
        </button>
        <button 
          className={`${styles["tab-button"]} ${activeTab === 'additional' ? styles.active : ''}`}
          onClick={() => setActiveTab('additional')}
        >
          Additional Info
        </button>
        <button 
          className={`${styles["tab-button"]} ${activeTab === 'backups' ? styles.active : ''}`}
          onClick={() => setActiveTab('backups')}
        >
          Backups
        </button>
        <button 
          className={`${styles["tab-button"]} ${activeTab === 'print' ? styles.active : ''}`}
          onClick={() => setActiveTab('print')}
        >
          Print
        </button>
      </div>
        </>
      )}
      
      <div className={styles["tab-content"]}>
        {activeTab === 'additional' && (
          <div className={styles["additional-info"]}>
            <h2>Additional Information</h2>
            
            <div className={styles["section-content"]}>
              <div className={styles["field-group"]}>
                <label>
                  <input 
                    type="checkbox" 
                    checked={showSefirasHaomer}
                    onChange={(e) => {
                      setShowSefirasHaomer(e.target.checked);
                      setSchedule(prevSchedule => ({
                        ...prevSchedule,
                        displayOptions: {
                          ...prevSchedule.displayOptions,
                          showSefirasHaomer: e.target.checked
                        }
                      }));
                    }}
                  />
                  Show Sefiras Ha'omer
                </label>
              </div>

              <div className={styles["field-group"]}>
                <label>
                  <input 
                    type="checkbox" 
                    checked={showThreeKapitels}
                    onChange={(e) => {
                      setShowThreeKapitels(e.target.checked);
                      setSchedule(prevSchedule => ({
                        ...prevSchedule,
                        displayOptions: {
                          ...prevSchedule.displayOptions,
                          showThreeKapitels: e.target.checked
                        }
                      }));
                    }}
                  />
                  Show 3 Kapitels Tehillim (Elul–Yom Kippur)
                </label>
              </div>
              
              <div className={styles["field-group"]}>
                <h3>Community Updates</h3>
                <div className={styles["list-editor"]}>
                  {newsItems.map((item, index) => (
                    <div key={index} className={styles["list-item"]}>
                      <input 
                        type="text" 
                        dir={INPUT_DIR_MIXED}
                        value={item} 
                        onChange={(e) => {
                          const updatedItems = [...newsItems];
                          updatedItems[index] = e.target.value;
                          setNewsItems(updatedItems);
                          setSchedule(prevSchedule => ({
                            ...prevSchedule,
                            news: updatedItems
                          }));
                        }}
                      />
                      <button 
                        className={styles["remove-button"]}
                        onClick={() => {
                          const updatedItems = [...newsItems];
                          updatedItems.splice(index, 1);
                          setNewsItems(updatedItems);
                          setSchedule(prevSchedule => ({
                            ...prevSchedule,
                            news: updatedItems
                          }));
                        }}
                        title="Remove"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                  <button 
                    className={styles["add-button"]}
                    onClick={() => {
                      const updatedItems = [...newsItems, ''];
                      setNewsItems(updatedItems);
                      setSchedule(prevSchedule => ({
                        ...prevSchedule,
                        news: updatedItems
                      }));
                    }}
                    title="Add Item"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add News Item
                  </button>
                </div>
              </div>
              
              <div className={styles["field-group"]}>
                <h3>Weekly Notices</h3>
                <div className={styles["list-editor"]}>
                  {weeklyNotices.map((item, index) => (
                    <div key={index} className={styles["list-item"]}>
                      <input 
                        type="text" 
                        dir={INPUT_DIR_MIXED}
                        value={item} 
                        onChange={(e) => {
                          const updatedItems = [...weeklyNotices];
                          updatedItems[index] = e.target.value;
                          setWeeklyNotices(updatedItems);
                          setSchedule(prevSchedule => ({
                            ...prevSchedule,
                            weeklyNotices: updatedItems
                          }));
                        }}
                      />
                      <button 
                        className={styles["remove-button"]}
                        onClick={() => {
                          const updatedItems = [...weeklyNotices];
                          updatedItems.splice(index, 1);
                          setWeeklyNotices(updatedItems);
                          setSchedule(prevSchedule => ({
                            ...prevSchedule,
                            weeklyNotices: updatedItems
                          }));
                        }}
                        title="Remove"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                  <button 
                    className={styles["add-button"]}
                    onClick={() => {
                      const updatedItems = [...weeklyNotices, ''];
                      setWeeklyNotices(updatedItems);
                      setSchedule(prevSchedule => ({
                        ...prevSchedule,
                        weeklyNotices: updatedItems
                      }));
                    }}
                    title="Add Item"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Weekly Notice
                  </button>
                </div>
              </div>
              
              <div className={styles["field-group"]}>
                <h3>Announcements</h3>
                
                {!showAnnouncementEditor ? (
                  <div>
                    <button 
                      onClick={openAnnouncementEditor}
                      className={styles["edit-button"]}
                      style={{ marginBottom: '10px' }}
                    >
                      Edit Announcements
                    </button>
                    <div className={styles["current-announcements"]}>
                      <div dangerouslySetInnerHTML={{ __html: announcements }} />
                    </div>
                  </div>
                ) : (
                  <div className={styles["announcement-editor"]}>
                    <div className={styles["editor-header"]}>
                      <h4>Edit Announcements</h4>
                      <div>
                        <button onClick={addAnnouncementBlock} className={styles["add-button"]}>
                          <FontAwesomeIcon icon={faPlus} /> Add Block
                        </button>
                        <button onClick={saveAnnouncementBlocks} className={styles["save-button"]}>
                          Save
                        </button>
                        <button onClick={() => setShowAnnouncementEditor(false)} className={styles["cancel-button"]}>
                          Cancel
                        </button>
                      </div>
                    </div>
                    
                    {announcementBlocks.map(block => (
                      <div key={block.id} className={styles["announcement-block"]}>
                        <div className={styles["block-header"]}>
                          <div className={styles["block-controls"]}>
                            <button 
                              onClick={() => updateAnnouncementBlock(block.id, 'visible', !block.visible)}
                              className={block.visible ? styles["visible"] : styles["hidden"]}
                              title={block.visible ? 'Hide' : 'Show'}
                            >
                              <FontAwesomeIcon icon={block.visible ? faEye : faEyeSlash} />
                            </button>
                            <button 
                              onClick={() => deleteAnnouncementBlock(block.id)} 
                              className={styles["delete"]}
                              title="Delete"
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                          </div>
                        </div>
                        
                        <div className={styles["block-settings"]}>
                          <div className={styles["setting-group"]}>
                            <label>Font Size:</label>
                            <select 
                              value={block.fontSize} 
                              onChange={(e) => updateAnnouncementBlock(block.id, 'fontSize', e.target.value)}
                            >
                              <option value="12px">12px</option>
                              <option value="14px">14px</option>
                              <option value="16px">16px</option>
                              <option value="18px">18px</option>
                              <option value="20px">20px</option>
                            </select>
                          </div>
                          
                          <div className={styles["setting-group"]}>
                            <label>Text Align:</label>
                            <select 
                              value={block.textAlign} 
                              onChange={(e) => updateAnnouncementBlock(block.id, 'textAlign', e.target.value)}
                            >
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                              <option value="left">Left</option>
                            </select>
                          </div>
                          
                          <div className={styles["setting-group"]}>
                            <label>Direction:</label>
                            <select 
                              value={block.direction} 
                              onChange={(e) => updateAnnouncementBlock(block.id, 'direction', e.target.value)}
                            >
                              <option value="rtl">RTL</option>
                              <option value="ltr">LTR</option>
                            </select>
                          </div>
                          
                          <div className={styles["setting-group"]}>
                            <label>Font Weight:</label>
                            <select 
                              value={block.fontWeight} 
                              onChange={(e) => updateAnnouncementBlock(block.id, 'fontWeight', e.target.value)}
                            >
                              <option value="normal">Normal</option>
                              <option value="bold">Bold</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className={styles["block-content"]}>
                          <textarea
                            dir={INPUT_DIR_MIXED}
                            value={block.content}
                            onChange={(e) => updateAnnouncementBlock(block.id, 'content', e.target.value)}
                            placeholder="Enter content here... (newlines are automatically converted to <br />, supports HTML like <ul><li>item</li></ul> for lists)"
                            rows={4}
                          />
                        </div>
                        
                        <div className={styles["block-preview"]}>
                          <h4>Preview:</h4>
                          <div 
                            style={{
                              fontSize: block.fontSize,
                              direction: block.direction,
                              textAlign: block.textAlign,
                              fontWeight: block.fontWeight,
                              border: '1px solid #ddd',
                              padding: '10px',
                              backgroundColor: block.visible ? 'white' : '#f0f0f0',
                              opacity: block.visible ? 1 : 0.5,
                              unicodeBidi: 'plaintext',
                            }}
                            dangerouslySetInnerHTML={{ __html: block.content.replace(/\n/g, '<br />') }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'weekly' && (
          <div className={styles["weekly-schedules"]}>
            <h2>Weekly Schedules</h2>
            
            {/* Week Selector */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '15px', 
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Preview Week:</label>
              <button
                onClick={() => navigateWeek('prev')}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
                title="Previous Week"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <input
                type="date"
                value={(() => {
                  const year = selectedWeekDate.getFullYear();
                  const month = String(selectedWeekDate.getMonth() + 1).padStart(2, '0');
                  const day = String(selectedWeekDate.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                })()}
                onChange={(e) => {
                  const newDate = new Date(e.target.value + 'T12:00:00');
                  setSelectedWeekDate(getSaturdayOfWeek(newDate));
                }}
                style={{
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <button
                onClick={() => navigateWeek('next')}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
                title="Next Week"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <span style={{ marginLeft: '10px', color: '#6c757d', fontSize: '14px' }}>
                {formatWeekDisplay(selectedWeekDate)}
              </span>
            </div>
            
            {Object.keys(schedule.weeklySchedules).map(sectionKey => (
              <div key={sectionKey} className={styles["schedule-section"]}>
                <div className={`${styles["section-content"]} ${isMobile && !expandedSections[`weekly-${sectionKey}`] ? styles.hidden : ''}`}
                  onClick={isMobile ? () => toggleSectionExpanded(sectionKey, 'weekly') : undefined}
                >
                  <div className={styles["section-header-bar"]}>
                    {isMobile && (
                      <button 
                        className={styles["section-toggle"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSectionExpanded(sectionKey, 'weekly');
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={expandedSections[`weekly-${sectionKey}`] ? faChevronUp : faChevronDown} 
                        />
                      </button>
                    )}
                    <div className={styles["compact-controls"]}>
                      <label>
                        Title:
                        <input 
                          type="text" 
                          dir={INPUT_DIR_MIXED}
                          value={schedule.weeklySchedules[sectionKey].sectionTitle || ''} 
                          onChange={(e) => handleWeeklySectionChange(sectionKey, 'sectionTitle', e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className={`${styles["schedule-items"]} ${isMobile && !expandedSections[`weekly-${sectionKey}`] ? styles.hidden : ''}`}>
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Value Type</th>
                        <th>Value</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.weeklySchedules[sectionKey].schedule.map((item, index) => {
                        const isExpanded = expandedItems[`weekly-${sectionKey}-${index}`];
                        
                        return (
                          <React.Fragment key={index}>
                            <tr>
                              <td data-label="Title">
                                <input 
                                  type="text" 
                                  dir={INPUT_DIR_MIXED}
                                  value={item.title || ''} 
                                  onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'title', e.target.value)}
                                />
                              </td>
                              <td data-label="Type">
                                <select 
                                  value={
                                    item.relativeZman !== undefined ? 'relative_zman' : 
                                    item.zman !== undefined ? 'zman' : 
                                    item.time !== undefined ? 'time' : 'value'
                                  } 
                                  onChange={(e) => {
                                    const valueType = e.target.value;
                                    const updatedItem = { ...item };
                                    
                                    // Remove existing value types
                                    delete updatedItem.value;
                                    delete updatedItem.zman;
                                    delete updatedItem.time;
                                    delete updatedItem.relativeZman;
                                    delete updatedItem.relativeMinutes;
                                    delete updatedItem.relativeDirection;
                                    
                                    // Add the new value type
                                    if (valueType === 'value') {
                                      updatedItem.value = '';
                                    } else if (valueType === 'zman') {
                                      updatedItem.zman = '';
                                    } else if (valueType === 'time') {
                                      updatedItem.time = '';
                                    } else if (valueType === 'relative_zman') {
                                      updatedItem.relativeZman = '';
                                      updatedItem.relativeMinutes = 10;
                                      updatedItem.relativeDirection = 'before_exact';
                                    }
                                    
                                    // Update the item
                                    setSchedule(prevSchedule => {
                                      const updatedItems = [...prevSchedule.weeklySchedules[sectionKey].schedule];
                                      updatedItems[index] = updatedItem;
                                      
                                      return {
                                        ...prevSchedule,
                                        weeklySchedules: {
                                          ...prevSchedule.weeklySchedules,
                                          [sectionKey]: {
                                            ...prevSchedule.weeklySchedules[sectionKey],
                                            schedule: updatedItems
                                          }
                                        }
                                      };
                                    });
                                  }}
                                >
                                  <option value="value">Free Text</option>
                                  <option value="time">Time</option>
                                  <option value="zman">Zman</option>
                                  <option value="relative_zman">Relative Zman</option>
                                </select>
                              </td>
                              <td data-label="Value">
                                {item.zman !== undefined ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <select 
                                      value={item.zman || ''} 
                                      onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'zman', e.target.value)}
                                    >
                                      <option value="">Select a Zman</option>
                                      {generateZmanOptions()}
                                    </select>
                                    {item.zman && (() => {
                                      const weeklyZmanimObj = weeklyZmanim(selectedWeekDate);
                                      const zmanimForSection = weeklyZmanimObj[sectionKey === 'shabbosDay_mevorchim' ? 'SHABBOS_DAY' : getWeeklyZmanimEnum(sectionKey)];
                                      const preview = getZmanPreview({...item}, zmanimForSection);
                                      return preview ? (
                                        <div 
                                          key={`preview-${item.zman}`}
                                          className={styles["zman-preview"]}
                                        >
                                          {preview}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : item.relativeZman !== undefined ? (
                                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                      <input 
                                        type="number" 
                                        value={item.relativeMinutes || 10} 
                                        onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'relativeMinutes', parseInt(e.target.value))}
                                        className={styles.zmanDigitBox}
                                        min="1"
                                      />
                                      <span style={{ width: '25px' }}>min.</span>
                                      <select 
                                        value={item.relativeDirection || 'before_exact'} 
                                        onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'relativeDirection', e.target.value)}
                                        style={{ width: '120px' }}
                                      >
                                        <option value="before_exact">before (exact)</option>
                                        <option value="before_roundDown">before (round down)</option>
                                        <option value="before_roundUp">before (round up)</option>
                                        <option value="after_exact">after (exact)</option>
                                        <option value="after_roundDown">after (round down)</option>
                                        <option value="after_roundUp">after (round up)</option>
                                      </select>
                                      <select 
                                        value={item.relativeZman || ''} 
                                        onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'relativeZman', e.target.value)}
                                      >
                                        <option value="">Select a Zman</option>
                                        {generateZmanOptions()}
                                      </select>
                                    </div>
                                    {item.relativeZman && (() => {
                                      const weeklyZmanimObj = weeklyZmanim(selectedWeekDate);
                                      const zmanimForSection = weeklyZmanimObj[sectionKey === 'shabbosDay_mevorchim' ? 'SHABBOS_DAY' : getWeeklyZmanimEnum(sectionKey)];
                                      const preview = getZmanPreview({...item}, zmanimForSection);
                                      return preview ? (
                                        <div 
                                          key={`preview-${item.relativeZman}-${item.relativeMinutes}-${item.relativeDirection}`}
                                          className={styles["zman-preview"]}
                                        >
                                          {preview}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : item.time !== undefined ? (
                                  <input
                                    type="text"
                                    dir={INPUT_DIR_LTR}
                                    value={item.time || ''}
                                    onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'time', e.target.value)}
                                    placeholder="e.g. 9:00 am"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    dir={INPUT_DIR_MIXED}
                                    value={item.value || ''}
                                    onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'value', e.target.value)}
                                  />
                                )}
                              </td>
                              <td data-label="Actions">
                                <div className={styles["move-buttons-container"]}>
                                  <button
                                    className={styles["remove-button"]}
                                    onClick={() => handleMoveWeeklyItemUp(sectionKey, index)}
                                    disabled={index === 0}
                                    title="Move Up"
                                  >
                                    <FontAwesomeIcon icon={faChevronUp} />
                                  </button>
                                  <button 
                                    className={styles["remove-button"]}
                                    onClick={() => handleMoveWeeklyItemDown(sectionKey, index)}
                                    disabled={index === schedule.weeklySchedules[sectionKey].schedule.length - 1}
                                    title="Move Down"
                                  >
                                    <FontAwesomeIcon icon={faChevronDown} />
                                  </button>
                                </div>
                                <button 
                                  className={styles["remove-button"]}
                                  onClick={() => handleRemoveWeeklyItem(sectionKey, index)}
                                  title="Remove"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                                <button 
                                  className={styles["expand-button"]}
                                  onClick={() => toggleItemExpanded(sectionKey, index, 'weekly')}
                                  title={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} />
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className={styles["expanded-row"]}>
                                <td colSpan="4">
                                  <div className={styles["expanded-fields"]}>
                                    <FontSizeFields
                                      fontSize={item.fontSize}
                                      onChange={(value) => handleWeeklyItemChange(sectionKey, index, 'fontSize', value)}
                                      lastManualRef={lastManualFontSizeRef}
                                      itemKey={`weekly-${sectionKey}-${index}`}
                                    />
                                    <div className={styles["field-group"]}>
                                      <label>Display:</label>
                                      <select 
                                        value={item.display || ''} 
                                        onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'display', e.target.value)}
                                      >
                                        <option value="">Default</option>
                                        <option value="print_only">Print Only</option>
                                        <option value="screen_only">Screen Only</option>
                                        <option value="none">None</option>
                                      </select>
                                    </div>
                                    <div className={styles["field-group"]}>
                                      <label>Byline:</label>
                                      <input 
                                        type="text" 
                                        dir={INPUT_DIR_MIXED}
                                        value={item.byline || ''} 
                                        onChange={(e) => handleWeeklyItemChange(sectionKey, index, 'byline', e.target.value)}
                                        placeholder="e.g. Rabbi Smith"
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  
                  <div className={styles["item-actions-row"]}>
                    <button 
                      className={styles["add-button"]}
                      onClick={() => handleAddWeeklyItem(sectionKey)}
                      title="Add Item"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Item
                    </button>
                    <div className={styles["sort-control"]}>
                      <label>
                        Sort Order:
                        <input 
                          type="number" 
                          value={schedule.weeklySchedules[sectionKey].sortOrder || 0} 
                          onChange={(e) => handleWeeklySectionChange(sectionKey, 'sortOrder', parseInt(e.target.value))}
                          style={{ width: '60px', marginLeft: '8px' }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {activeTab === 'dated' && (
          <div className={styles["dated-schedules"]}>
            <h2>Dated Schedules</h2>
            
            <button 
              className={styles["add-section-button"]}
              onClick={handleAddDatedSection}
              title="Add New Dated Schedule"
            >
              <FontAwesomeIcon icon={faPlus} /> Add New Dated Schedule
            </button>
            
            {Object.keys(schedule.datedSchedules).map(sectionKey => (
              <div key={sectionKey} className={styles["schedule-section"]}>
                <div className={`${styles["section-content"]} ${isMobile && !expandedSections[`dated-${sectionKey}`] ? styles.hidden : ''}`}
                  onClick={isMobile ? () => toggleSectionExpanded(sectionKey, 'dated') : undefined}
                >
                  <div className={styles["section-header-bar"]}>
                    {isMobile && (
                      <button 
                        className={styles["section-toggle"]}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSectionExpanded(sectionKey, 'dated');
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={expandedSections[`dated-${sectionKey}`] ? faChevronUp : faChevronDown} 
                        />
                      </button>
                    )}
                    <div className={styles["datetime-controls"]}>
                      <label className={styles["inline-label"]}>
                        Title:
                        <input 
                          type="text" 
                          dir={INPUT_DIR_MIXED}
                          value={schedule.datedSchedules[sectionKey].sectionTitle || ''} 
                          onChange={(e) => handleDatedSectionChange(sectionKey, 'sectionTitle', e.target.value)}
                        />
                      </label>
                      <label className={styles["inline-label"]}>
                        From:
                        <input 
                          type="datetime-local" 
                          value={schedule.datedSchedules[sectionKey].displayDateTime ? schedule.datedSchedules[sectionKey].displayDateTime.substring(0, 16) : ''} 
                          onChange={(e) => handleDatedSectionChange(sectionKey, 'displayDateTime', `${e.target.value}:00`)}
                        />
                      </label>
                      <label className={styles["inline-label"]}>
                        Until:
                        <input 
                          type="datetime-local" 
                          value={schedule.datedSchedules[sectionKey].expirationDateTime ? schedule.datedSchedules[sectionKey].expirationDateTime.substring(0, 16) : ''} 
                          onChange={(e) => handleDatedSectionChange(sectionKey, 'expirationDateTime', `${e.target.value}:00`)}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className={`${styles["schedule-items"]} ${isMobile && !expandedSections[`dated-${sectionKey}`] ? styles.hidden : ''}`}>
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Value Type</th>
                        <th>Value</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.datedSchedules[sectionKey].schedule.map((item, index) => {
                        const isExpanded = expandedItems[`dated-${sectionKey}-${index}`];
                        
                        return (
                          <React.Fragment key={index}>
                            <tr>
                              <td data-label="Title">
                                <input 
                                  type="text" 
                                  dir={INPUT_DIR_MIXED}
                                  value={item.title || ''} 
                                  onChange={(e) => handleDatedItemChange(sectionKey, index, 'title', e.target.value)}
                                />
                              </td>
                              <td data-label="Type">
                                <select 
                                  value={
                                    item.relativeZman !== undefined ? 'relative_zman' : 
                                    item.zman !== undefined ? 'zman' : 
                                    item.time !== undefined ? 'time' : 'value'
                                  } 
                                  onChange={(e) => {
                                    const valueType = e.target.value;
                                    const updatedItem = { ...item };
                                    
                                    // Remove existing value types
                                    delete updatedItem.value;
                                    delete updatedItem.zman;
                                    delete updatedItem.time;
                                    delete updatedItem.relativeZman;
                                    delete updatedItem.relativeMinutes;
                                    delete updatedItem.relativeDirection;
                                    
                                    // Add the new value type
                                    if (valueType === 'value') {
                                      updatedItem.value = '';
                                    } else if (valueType === 'zman') {
                                      updatedItem.zman = '';
                                    } else if (valueType === 'time') {
                                      updatedItem.time = '';
                                    } else if (valueType === 'relative_zman') {
                                      updatedItem.relativeZman = '';
                                      updatedItem.relativeMinutes = 10;
                                      updatedItem.relativeDirection = 'before_exact';
                                    }
                                    
                                    // Update the item
                                    setSchedule(prevSchedule => {
                                      const updatedItems = [...prevSchedule.datedSchedules[sectionKey].schedule];
                                      updatedItems[index] = updatedItem;
                                      
                                      return {
                                        ...prevSchedule,
                                        datedSchedules: {
                                          ...prevSchedule.datedSchedules,
                                          [sectionKey]: {
                                            ...prevSchedule.datedSchedules[sectionKey],
                                            schedule: updatedItems
                                          }
                                        }
                                      };
                                    });
                                  }}
                                >
                                  <option value="value">Free Text</option>
                                  <option value="time">Time</option>
                                  <option value="zman">Zman</option>
                                  <option value="relative_zman">Relative Zman</option>
                                </select>
                              </td>
                              <td data-label="Value">
                                {item.zman !== undefined ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <select 
                                      value={item.zman || ''} 
                                      onChange={(e) => handleDatedItemChange(sectionKey, index, 'zman', e.target.value)}
                                    >
                                      <option value="">Select a Zman</option>
                                      {generateZmanOptions()}
                                    </select>
                                    {item.zman && (() => {
                                      const targetDate = item.date ? new Date(item.date) :
                                        schedule.datedSchedules[sectionKey].displayDateTime ?
                                        new Date(schedule.datedSchedules[sectionKey].displayDateTime) :
                                        new Date();
                                      const preview = getZmanPreview({...item}, getDailyZmanim(targetDate));
                                      return preview ? (
                                        <div className={styles["zman-preview"]}>
                                          {preview}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : item.relativeZman !== undefined ? (
                                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                      <input 
                                        type="number" 
                                        value={item.relativeMinutes || 10} 
                                        onChange={(e) => handleDatedItemChange(sectionKey, index, 'relativeMinutes', parseInt(e.target.value))}
                                        className={styles.zmanDigitBox}
                                        min="1"
                                      />
                                      <span style={{ width: '25px' }}>min.</span>
                                      <select 
                                        value={item.relativeDirection || 'before_exact'} 
                                        onChange={(e) => handleDatedItemChange(sectionKey, index, 'relativeDirection', e.target.value)}
                                        style={{ width: '120px' }}
                                      >
                                        <option value="before_exact">before (exact)</option>
                                        <option value="before_roundDown">before (round down)</option>
                                        <option value="before_roundUp">before (round up)</option>
                                        <option value="after_exact">after (exact)</option>
                                        <option value="after_roundDown">after (round down)</option>
                                        <option value="after_roundUp">after (round up)</option>
                                      </select>
                                      <select 
                                        value={item.relativeZman || ''} 
                                        onChange={(e) => handleDatedItemChange(sectionKey, index, 'relativeZman', e.target.value)}
                                      >
                                        <option value="">Select a Zman</option>
                                        {generateZmanOptions()}
                                      </select>
                                    </div>
                                    {item.relativeZman && (() => {
                                      const targetDate = item.date ? new Date(item.date) :
                                        schedule.datedSchedules[sectionKey].displayDateTime ?
                                        new Date(schedule.datedSchedules[sectionKey].displayDateTime) :
                                        new Date();
                                      const preview = getZmanPreview({...item}, getDailyZmanim(targetDate));
                                      return preview ? (
                                        <div className={styles["zman-preview"]}>
                                          {preview}
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : item.time !== undefined ? (
                                  <input 
                                    type="text" 
                                    dir={INPUT_DIR_LTR}
                                    value={item.time || ''} 
                                    onChange={(e) => handleDatedItemChange(sectionKey, index, 'time', e.target.value)}
                                    placeholder="e.g. 9:00 am"
                                  />
                                ) : (
                                  <input 
                                    type="text" 
                                    dir={INPUT_DIR_MIXED}
                                    value={item.value || ''} 
                                    onChange={(e) => handleDatedItemChange(sectionKey, index, 'value', e.target.value)}
                                  />
                                )}
                              </td>
                              <td data-label="Date">
                                <input 
                                  type="date" 
                                  value={item.date ? item.date.split('T')[0] : ''} 
                                  onChange={(e) => handleDatedItemChange(sectionKey, index, 'date', e.target.value)}
                                />
                              </td>
                              <td data-label="Actions">
                                <div className={styles["move-buttons-container"]}>
                                  <button 
                                    className={styles["remove-button"]}
                                    onClick={() => handleMoveDatedItemUp(sectionKey, index)}
                                    disabled={index === 0}
                                    title="Move Up"
                                  >
                                    <FontAwesomeIcon icon={faChevronUp} />
                                  </button>
                                  <button 
                                    className={styles["remove-button"]}
                                    onClick={() => handleMoveDatedItemDown(sectionKey, index)}
                                    disabled={index === schedule.datedSchedules[sectionKey].schedule.length - 1}
                                    title="Move Down"
                                  >
                                    <FontAwesomeIcon icon={faChevronDown} />
                                  </button>
                                </div>
                                <button 
                                  className={styles["remove-button"]}
                                  onClick={() => handleRemoveDatedItem(sectionKey, index)}
                                  title="Remove"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                                <button 
                                  className={styles["expand-button"]}
                                  onClick={() => toggleItemExpanded(sectionKey, index, 'dated')}
                                  title={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus} />
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className={styles["expanded-row"]}>
                                <td colSpan="5">
                                  <div className={styles["expanded-fields"]}>
                                    <FontSizeFields
                                      fontSize={item.fontSize}
                                      onChange={(value) => handleDatedItemChange(sectionKey, index, 'fontSize', value)}
                                      lastManualRef={lastManualFontSizeRef}
                                      itemKey={`dated-${sectionKey}-${index}`}
                                    />
                                    <div className={styles["field-group"]}>
                                      <label>Display:</label>
                                      <select 
                                        value={item.display || ''} 
                                        onChange={(e) => handleDatedItemChange(sectionKey, index, 'display', e.target.value)}
                                      >
                                        <option value="">Default</option>
                                        <option value="print_only">Print Only</option>
                                        <option value="screen_only">Screen Only</option>
                                        <option value="none">None</option>
                                      </select>
                                    </div>
                                    <div className={styles["field-group"]}>
                                      <label>Byline:</label>
                                      <input 
                                        type="text" 
                                        dir={INPUT_DIR_MIXED}
                                        value={item.byline || ''} 
                                        onChange={(e) => handleDatedItemChange(sectionKey, index, 'byline', e.target.value)}
                                        placeholder="e.g. Rabbi Smith"
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  
                  <div className={styles["item-actions-row"]}>
                    <button 
                      className={styles["add-button"]}
                      onClick={() => handleAddDatedItem(sectionKey)}
                      title="Add Item"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Item
                    </button>
                    <div className={styles["sort-control"]}>
                      <label>
                        Sort Order:
                        <input 
                          type="number" 
                          value={schedule.datedSchedules[sectionKey].sortOrder || 0} 
                          onChange={(e) => handleDatedSectionChange(sectionKey, 'sortOrder', parseInt(e.target.value))}
                          style={{ width: '60px', marginLeft: '8px' }}
                        />
                      </label>
                    </div>
                    <button 
                      className={styles["remove-section-button"]}
                      onClick={() => handleRemoveDatedSection(sectionKey)}
                      title="Remove Section"
                    >
                      <FontAwesomeIcon icon={faTrash} /> Remove Section
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {activeTab === 'weekly' && (
        <div className={styles["weekly-actions"]}>
          {defaultWeeklySchedules && (
            <div className={styles["default-actions"]}>
              <div className={styles["default-info"]}>
                {defaultSavedAt && (
                  <div className={styles["default-saved-at"]}>
                    Default weekly schedules last saved: {formatDate(defaultSavedAt)}
                  </div>
                )}
                {defaultSuccess && (
                  <div className={styles["default-success"]}>Operation completed successfully!</div>
                )}
                {defaultError && (
                  <div className={styles["default-error"]}>{defaultError}</div>
                )}
              </div>
              <div className={styles["default-buttons"]}>
                <button 
                  className={`${styles["default-button"]} ${styles["save-default"]}`}
                  onClick={handleSaveDefault}
                  disabled={savingDefault}
                >
                  {savingDefault ? 'Saving...' : 'Save Current as Default'}
                </button>
                <button 
                  className={`${styles["default-button"]} ${styles["restore-default"]}`}
                  onClick={handleRestoreDefault}
                  disabled={restoringDefault}
                >
                  {restoringDefault ? 'Restoring...' : 'Restore Default'}
                </button>
              </div>
            </div>
          )}
          {!defaultWeeklySchedules && (
            <div className={styles["default-actions"]}>
              <div className={styles["default-info"]}>
                <div className={styles["no-default"]}>No default weekly schedules saved yet.</div>
                {defaultSuccess && (
                  <div className={styles["default-success"]}>Default weekly schedules saved successfully!</div>
                )}
                {defaultError && (
                  <div className={styles["default-error"]}>{defaultError}</div>
                )}
              </div>
              <button 
                className={`${styles["default-button"]} ${styles["save-default"]}`}
                onClick={handleSaveDefault}
                disabled={savingDefault}
              >
                {savingDefault ? 'Saving...' : 'Save Current as Default'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'backups' && (
        <div className={styles["backups-container"]}>
          <h2>Schedule Backups</h2>
          
          {backupError && <div className={styles["error-message"]}>{backupError}</div>}
          
          <div className={styles["backups-content"]}>
            <div className={styles["backups-list"]}>
              <h3>Available Backups</h3>
              {backupsLoading ? (
                <div className={styles["loading-message"]}>Loading backups...</div>
              ) : backups.length === 0 ? (
                <div className={styles["no-backups-message"]}>No backups found.</div>
              ) : (
                <div className={styles["backup-items"]}>
                  {backups.map((entry) => (
                    <div 
                      key={entry.id}
                      className={`${styles["backup-item"]} ${selectedBackup === entry.id ? styles.selected : ''}`}
                      onClick={() => handleBackupSelect(entry.id)}
                    >
                      <div className={styles["backup-name"]}>{entry.section} (v{entry.version})</div>
                      <div className={styles["backup-date"]}>{new Date(entry.createdAt).toLocaleString()}{entry.changedBy ? ` by ${entry.changedBy}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {selectedBackup && (
              <div className={styles["backup-preview"]}>
                <h3>History Entry Preview</h3>
                {backupContent ? (
                  <div className={styles["backup-content"]}>
                    <pre>{JSON.stringify(backupContent.data, null, 2)}</pre>
                    
                    <div className={styles["backup-actions"]}>
                      <button 
                        className={styles["restore-button"]}
                        onClick={handleRestoreBackup}
                        disabled={restoring}
                      >
                        {restoring ? 'Restoring...' : 'Restore This Backup'}
                      </button>
                      <button 
                        className={styles["delete-button"]}
                        onClick={handleDeleteBackup}
                        disabled={deleting}
                      >
                        {deleting ? 'Deleting...' : 'Delete This Backup'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles["loading-message"]}>Loading backup content...</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'print' && (
        <div className={styles["print-container"]}>
          <h2>Print Configuration</h2>
          <p>Select and reorder sections to include in the print version.</p>
          
          {printError && <div className={styles["error-message"]}>{printError}</div>}
          {printSuccess && <div className={styles["success-message"]}>{printSuccess}</div>}
          
          {/* Named Configuration Management */}
          <div className={styles["named-configs-section"]}>
            <h3>Saved Configurations</h3>
            
            {/* Save Configuration */}
            <div className={styles["save-config-section"]}>
              <div className={styles["input-group"]}>
                <input
                  type="text"
                  dir={INPUT_DIR_LTR}
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  placeholder="Enter configuration name..."
                  className={styles["config-name-input"]}
                />
                <button
                  className={styles["save-config-button"]}
                  onClick={saveNamedConfig}
                  disabled={savingConfig || !configName.trim()}
                >
                  {savingConfig ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>
            
            {/* Load/Delete Configurations */}
            <div className={styles["saved-configs-list"]}>
              <h4>Available Configurations:</h4>
              {loadingConfigs ? (
                <div className={styles["loading-message"]}>Loading configurations...</div>
              ) : savedConfigs.length === 0 ? (
                <div className={styles["no-configs"]}>No saved configurations</div>
              ) : (
                <div className={styles["configs-list"]}>
                  {savedConfigs.map((config) => (
                    <div key={config} className={styles["config-item"]}>
                      <span className={styles["config-name"]}>{config}</span>
                      <div className={styles["config-actions"]}>
                        <button
                          className={styles["load-config-button"]}
                          onClick={() => loadNamedConfig(config)}
                          disabled={loadingConfig}
                        >
                          {loadingConfig ? 'Loading...' : 'Load'}
                        </button>
                        <button
                          className={styles["delete-config-button"]}
                          onClick={() => deleteNamedConfig(config)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          
          <div className={styles["print-content"]}>
            <div className={styles["section-picker"]}>
              <h3>Available Sections</h3>
              <div className={styles["section-lists"]}>
                <div className={styles["weekly-sections"]}>
                  <h4>Weekly Schedules</h4>
                  {schedule?.weeklySchedules ? (
                    Object.entries(schedule.weeklySchedules).map(([key, section]) => (
                      <div key={key} className={styles["section-item"]}>
                        <span className={styles["section-title"]}>{section.sectionTitle || key}</span>
                        <button 
                          className={styles["add-button"]}
                          onClick={() => addSectionToPrint(key, 'weekly')}
                          disabled={printConfig.selectedSections.some(s => s.key === key)}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className={styles["no-sections"]}>No weekly schedules available</div>
                  )}
                </div>
                <div className={styles["dated-sections"]}>
                  <h4>Dated Schedules</h4>
                  {schedule?.datedSchedules ? (
                    Object.entries(schedule.datedSchedules).map(([key, section]) => (
                      <div key={key} className={styles["section-item"]}>
                        <span className={styles["section-title"]}>{section.sectionTitle || key}</span>
                        <button 
                          className={styles["add-button"]}
                          onClick={() => addSectionToPrint(key, 'dated')}
                          disabled={printConfig.selectedSections.some(s => s.key === key)}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className={styles["no-sections"]}>No dated schedules available</div>
                  )}
                </div>
              </div>
            </div>
            
              <div className={styles["selected-sections"]}>
                <h3>Selected Sections (Print Order)</h3>
                <div className={styles["selected-list"]}>
                  {printConfig.selectedSections.length === 0 ? (
                    <div className={styles["no-sections"]}>No sections selected for print</div>
                  ) : (
                    printConfig.selectedSections
                      .filter(section => section.type !== 'page-break') // Skip page break sections
                      .map((section, index) => (
                      <div key={`${section.type}-${section.key}`} className={styles["selected-item"]}>
                        <div className={styles["reorder-controls"]}>
                          <button
                            className={styles["move-button"]}
                            onClick={() => moveSectionUp(index)}
                            disabled={index === 0}
                            title="Move up"
                          >
                            <FontAwesomeIcon icon={faChevronUp} />
                          </button>
                          <button
                            className={styles["move-button"]}
                            onClick={() => moveSectionDown(index)}
                            disabled={index === printConfig.selectedSections.length - 1}
                            title="Move down"
                          >
                            <FontAwesomeIcon icon={faChevronDown} />
                          </button>
                        </div>
                        <span className={styles["section-title"]}>
                          {section.title}
                        </span>
                        <span className={styles["section-type"]}>
                          ({section.type})
                        </span>
                        <button
                          className={styles["remove-button"]}
                          onClick={() => removeSectionFromPrint(index)}
                          title="Remove from print"
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              
              {printConfig.selectedSections.length > 0 && (
                <div className={styles["print-actions"]}>
                  <button 
                    className={styles["save-print-button"]}
                    onClick={savePrintConfigData}
                    disabled={printLoading}
                  >
                    {printLoading ? 'Saving...' : 'Save Print Configuration'}
                  </button>
                  <a 
                    href="/print" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles["view-print-button"]}
                  >
                    View Print Page
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className={styles["save-section"]}>
        {saveSuccess && (
          <div className={styles["save-success"]}>Schedule saved successfully!</div>
        )}
        {saveError && (
          <div className={styles["save-error"]}>{saveError}</div>
        )}
        <button 
          className={styles["save-button"]}
          onClick={handleSave}
          disabled={saving}
          title="Save Schedule (Ctrl+S)"
        >
          <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
          {saving ? 'Saving...' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
};

export default ScheduleEditor;
